import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X, Save, Loader2 } from "lucide-react";
import { pasarelasApi, PasarelaPago, TipoPasarela } from "../../lib/api";

/**
 * Esquemas de campos por tipo de pasarela.
 * Cada tipo define qué campos pide y si requieren credenciales secretas.
 */
const CAMPOS: Record<TipoPasarela, Array<{
  key: string; label: string; tipo?: "text" | "email" | "tel" | "password" | "url" | "textarea";
  placeholder?: string; ayuda?: string; required?: boolean; publico?: boolean;
}>> = {
  pago_movil: [
    { key: "banco", label: "Banco", placeholder: "Banco de Venezuela", required: true, publico: true },
    { key: "tipoIdentificacion", label: "Tipo de cédula", placeholder: "V / J / E", publico: true },
    { key: "cedula", label: "Cédula/RIF", placeholder: "V12345678", required: true, publico: true },
    { key: "telefono", label: "Teléfono", tipo: "tel", placeholder: "04141234567", required: true, publico: true },
  ],
  transferencia: [
    { key: "banco", label: "Banco", placeholder: "Banco de Venezuela", required: true, publico: true },
    { key: "beneficiario", label: "Beneficiario", placeholder: "Razón social o nombre", required: true, publico: true },
    { key: "rifCi", label: "RIF/CI", placeholder: "J123456789", required: true, publico: true },
    { key: "tipoCuenta", label: "Tipo de cuenta", placeholder: "Corriente / Ahorros", publico: true },
    { key: "numeroCuenta", label: "Número de cuenta", placeholder: "0102 0123 45 0001234567", required: true, publico: true },
    { key: "telefonoContacto", label: "Teléfono de contacto", tipo: "tel", placeholder: "Para confirmar pagos", publico: true },
  ],
  zelle: [
    { key: "beneficiario", label: "Beneficiario", placeholder: "Nombre que aparece en Zelle", required: true, publico: true },
    { key: "email", label: "Email Zelle", tipo: "email", placeholder: "pagos@ejemplo.com", publico: true },
    { key: "telefono", label: "Teléfono Zelle", tipo: "tel", placeholder: "+1 555 1234", publico: true },
  ],
  paypal: [
    { key: "emailPaypal", label: "Email PayPal (envío directo)", tipo: "email", placeholder: "pagos@ejemplo.com", publico: true },
    { key: "clientId", label: "Client ID (API)", tipo: "password", ayuda: "Solo si quieres checkout integrado", publico: false },
    { key: "secret", label: "Client Secret", tipo: "password", publico: false },
  ],
  stripe: [
    { key: "publishableKey", label: "Publishable Key", tipo: "password", placeholder: "pk_live_...", required: true, publico: true },
    { key: "secretKey", label: "Secret Key", tipo: "password", placeholder: "sk_live_...", required: true, publico: false },
    { key: "webhookSecret", label: "Webhook Secret", tipo: "password", placeholder: "whsec_...", publico: false },
  ],
  mercadopago: [
    { key: "publicKey", label: "Public Key", tipo: "password", placeholder: "APP_USR-...", required: true, publico: true },
    { key: "accessToken", label: "Access Token", tipo: "password", placeholder: "APP_USR-...", required: true, publico: false },
  ],
  efectivo: [],
  enlace_pago: [
    { key: "url", label: "URL del proveedor", tipo: "url", placeholder: "https://...", required: true, publico: true },
    { key: "metodo", label: "Método (POST/GET)", placeholder: "POST", publico: true },
    { key: "apiKey", label: "API Key (opcional)", tipo: "password", publico: false },
  ],
  api_custom: [
    { key: "url", label: "URL de la API", tipo: "url", placeholder: "https://...", required: true, publico: true },
    { key: "metodo", label: "Método", placeholder: "POST", publico: false },
    { key: "headers", label: "Headers (JSON)", tipo: "textarea", placeholder: '{"X-API-Key": "..."}', publico: false },
    { key: "payloadTemplate", label: "Payload template (JSON)", tipo: "textarea", publico: false,
      ayuda: "Variables: {{monto}}, {{moneda}}, {{cliente}}, {{referencia}}" },
  ],
};

export function EditarPasarelaModal({
  pasarela, nuevoTipo, open, onClose,
}: {
  pasarela: PasarelaPago | null;
  nuevoTipo: TipoPasarela | null;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const tipo = pasarela?.tipo || nuevoTipo;
  const editando = !!pasarela;

  const [nombre, setNombre] = useState("");
  const [modo, setModo] = useState<"sandbox" | "live">("sandbox");
  const [activa, setActiva] = useState(true);
  const [confirmacionAutomatica, setConfirmacionAutomatica] = useState(false);
  const [moneda, setMoneda] = useState("USD");
  const [orden, setOrden] = useState(0);
  const [instrucciones, setInstrucciones] = useState("");
  const [config, setConfig] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (pasarela) {
      setNombre(pasarela.nombre);
      setModo(pasarela.modo);
      setActiva(pasarela.activa);
      setConfirmacionAutomatica(pasarela.confirmacionAutomatica);
      setMoneda(pasarela.moneda);
      setOrden(pasarela.orden);
      setInstrucciones(pasarela.instrucciones || "");
      setConfig(pasarela.config || {});
    } else if (nuevoTipo) {
      const sugerido = CAMPOS[nuevoTipo].length === 0 ? "Efectivo" : "";
      setNombre(sugerido);
      setModo("sandbox");
      setActiva(true);
      setConfirmacionAutomatica(nuevoTipo === "paypal" || nuevoTipo === "stripe" || nuevoTipo === "mercadopago");
      setMoneda("USD");
      setOrden(0);
      setInstrucciones("");
      setConfig({});
    }
    setError(null);
  }, [pasarela, nuevoTipo]);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!tipo) throw new Error("Tipo no definido");
      const dto: any = {
        nombre, tipo, modo, activa, orden, moneda, config, instrucciones,
        confirmacionAutomatica,
      };
      if (editando) {
        return pasarelasApi.actualizar(Number(pasarela!.id), dto);
      } else {
        return pasarelasApi.crear(dto);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pasarelas"] });
      onClose();
    },
    onError: (e: any) => setError(e.message || "Error guardando"),
  });

  if (!open || !tipo) return null;

  const campos = CAMPOS[tipo] || [];

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in"
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>

        <div className="flex items-center justify-between px-5 py-3 border-b border-ink-200">
          <h2 className="font-display font-bold text-ink-800">
            {editando ? "Editar" : "Nueva"} pasarela · {tipo}
          </h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700">
            <X className="size-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {error && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Datos generales */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1">Nombre interno *</label>
              <input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)}
                placeholder="Ej: Pago Móvil BDV"
                className="input w-full" />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1">Moneda</label>
              <select value={moneda} onChange={(e) => setMoneda(e.target.value)} className="input w-full">
                <option>USD</option>
                <option>VED</option>
                <option>EUR</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <label className="flex items-center gap-2 text-sm text-ink-700 cursor-pointer">
              <input type="checkbox" checked={activa} onChange={(e) => setActiva(e.target.checked)}
                className="size-4" /> Activa
            </label>
            <label className="flex items-center gap-2 text-sm text-ink-700 cursor-pointer">
              <input type="checkbox" checked={modo === "live"}
                onChange={(e) => setModo(e.target.checked ? "live" : "sandbox")} className="size-4" />
              Modo live
            </label>
            <label className="flex items-center gap-2 text-sm text-ink-700 cursor-pointer">
              <input type="checkbox" checked={confirmacionAutomatica}
                onChange={(e) => setConfirmacionAutomatica(e.target.checked)} className="size-4" />
              Confirmación automática
            </label>
            <div>
              <label className="block text-xs text-ink-600 mb-0.5">Orden</label>
              <input type="number" value={orden} onChange={(e) => setOrden(Number(e.target.value))}
                className="input w-full" />
            </div>
          </div>

          {campos.length > 0 && (
            <>
              <hr className="border-ink-200" />
              <h3 className="text-sm font-bold text-ink-800">Configuración específica</h3>
              {campos.map((c) => (
                <div key={c.key}>
                  <label className="block text-xs font-medium text-ink-600 mb-1">
                    {c.label}{c.required && " *"}
                    {!c.publico && <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">privado</span>}
                  </label>
                  {c.tipo === "textarea" ? (
                    <textarea rows={3} value={config[c.key] || ""}
                      onChange={(e) => setConfig({ ...config, [c.key]: e.target.value })}
                      placeholder={c.placeholder}
                      className="input w-full font-mono text-xs" />
                  ) : (
                    <input type={c.tipo || "text"} value={config[c.key] || ""}
                      onChange={(e) => setConfig({ ...config, [c.key]: e.target.value })}
                      placeholder={c.placeholder}
                      className="input w-full" />
                  )}
                  {c.ayuda && <p className="text-xs text-ink-500 mt-1">{c.ayuda}</p>}
                </div>
              ))}
            </>
          )}

          <hr className="border-ink-200" />
          <div>
            <label className="block text-xs font-medium text-ink-600 mb-1">
              Instrucciones para el cliente
            </label>
            <textarea rows={3} value={instrucciones} onChange={(e) => setInstrucciones(e.target.value)}
              placeholder="Ej: Hacer pago móvil al banco/cédula/teléfono y luego reportar con el número de referencia"
              className="input w-full" />
            <p className="text-xs text-ink-500 mt-1">Se mostrarán al cliente al elegir este método de pago</p>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-ink-200 flex items-center justify-end gap-2 bg-ink-50">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button onClick={() => saveMut.mutate()} disabled={saveMut.isPending || !nombre.trim()}
            className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
            {saveMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
            {editando ? "Guardar" : "Crear"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
